import { useEffect, useMemo, useRef, useState } from "react";
import bookByPriceModel from "../../../data/Services/bookByPriceModel";
import { getTickSize } from "../../../utils/priceUtils";
import { settings } from "../../../data/Services/settings";
import {
  computeBookStatus,
  sliceDepth,
  deriveTopOfBook,
  buildFixedPriceLadder,
  STATUS,
} from "./bookByPriceHelpers";

/**
 * Subscribe a panel to normalized BookByPrice snapshots and derive UI-friendly
 * state from them.
 *
 * @param {string} symbol            symbol to subscribe to (empty → idle)
 * @param {string} subscriberId      stable id for this panel instance
 * @param {number} depth             fallback depth/side when runtime-config's
 *                                   bookByPriceRowsPerSide is absent
 * @param {object} [options]
 * @param {object} [options.model]   override model singleton (tests)
 * @param {boolean} [options.freezeGridCenter]
 *
 * @returns {{
 *   snapshot,
 *   bids, asks, ladder,
 *   bestBid, bestAsk, spread, midPrice,
 *   status, error
 * }}
 */
export function useBookByPriceSnapshot(
  symbol,
  subscriberId,
  depth,
  options = {},
) {
  const model = options.model || bookByPriceModel;
  const freezeGridCenter = options.freezeGridCenter ?? false;

  const [snapshot, setSnapshot] = useState(() =>
    symbol ? (model.getLastBookByPrice?.(symbol) ?? null) : null,
  );
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  // Subscribe on (symbol, subscriberId) change.
  useEffect(() => {
    if (!symbol || !subscriberId) return undefined;
    let active = true;
    setError(null);
    const cached = model.getLastBookByPrice?.(symbol);
    setSnapshot(cached ?? null);
    let unsubscribe = () => {};
    try {
      unsubscribe = model.subscribeBookByPrice(symbol, subscriberId, (snap) => {
        if (!active || !mountedRef.current) return;
        setSnapshot(snap);
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    }
    return () => {
      active = false;
      try {
        unsubscribe?.();
      } catch {
        /* noop */
      }
    };
  }, [symbol, subscriberId, model]);

  const status = computeBookStatus(snapshot, symbol, error);

  // Book depth per side — `bookByPriceRowsPerSide` from runtime-config.json
  // (default 80), falling back to the `depth` arg. Drives BOTH the Tradicional
  // slice and the SuperDOM grid so the two views show the same book depth.
  const perSide =
    Number(settings.bookByPriceRowsPerSide) || Number(depth) || 80;

  const { bids, asks } = useMemo(
    () => sliceDepth(snapshot, perSide),
    [snapshot, perSide],
  );

  // Persist grid center across renders; reset automatically when symbol changes.
  const gridRef = useRef({ symbol: null, centerTick: null });
  const tickSize = useMemo(
    () => (symbol ? getTickSize(symbol) || 0.01 : 0.01),
    [symbol],
  );
  // SuperDOM ladder height — perSide rows each side of the mid; clamped.
  const rowCount = Math.min(Math.max(perSide, 15), 200) * 2;

  const ladder = useMemo(() => {
    if (!snapshot || !symbol) {
      gridRef.current = { symbol, centerTick: null };
      return [];
    }
    if (gridRef.current.symbol !== symbol) {
      gridRef.current = { symbol, centerTick: null };
    }
    const centerRef = {
      get current() {
        return gridRef.current.centerTick;
      },
      set current(v) {
        gridRef.current.centerTick = v;
      },
    };
    return buildFixedPriceLadder(snapshot, tickSize, rowCount, centerRef, {
      freezeCenter: freezeGridCenter,
    });
  }, [freezeGridCenter, snapshot, symbol, tickSize, rowCount]);

  const { bestBid, bestAsk, spread, midPrice } = useMemo(
    () => deriveTopOfBook({ bids, asks }),
    [bids, asks],
  );

  return {
    snapshot,
    bids,
    asks,
    ladder,
    bestBid,
    bestAsk,
    spread,
    midPrice,
    status,
    error,
  };
}

export { STATUS };
